import { useTranslate } from 'module/hooks/use-translation';
import {
  HumanisedDateWithYear,
  HumanisedTime,
} from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Tag } from 'module/ui/components/atoms/tag';
import {
  Body01,
  Body02,
  ProductiveHeading01,
  ProductiveHeading03,
} from 'module/ui/ozone/atoms';
import {
  faAlignLeft,
  faCalendar,
  faCircleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { Timeline } from 'api/timeline/types';
import moment from 'moment';
import { Fragment, memo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { EmptyState } from 'components/atoms/empty-state';
import {
  FlexWrapperWithJustifyBetween,
  FlexWrapperWithShortGap,
  FlexWrapperWithShortGapColumn,
} from 'components/common/styles';
import { SignalOutputCard } from 'components/organisms/signal-output/index';
import { signalTypeConfigs } from '../signal/signal-type/type-configs';

const TimelineHeader = styled.div`
  margin-bottom: var(--spacing-05);
`;

const RecapSection = styled.div`
  margin-top: var(--spacing-05);
  margin-bottom: var(--spacing-05);
`;

const RecapTitle = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  margin-bottom: var(--spacing-05);
`;

const RecapText = styled.p`
  color: var(--gray-100);
  font-size: var(--font-size-base);
  line-height: var(--line-height-05);
  margin-bottom: var(--spacing-05);
`;

const TagsContainer = styled.div`
  display: flex;
  gap: var(--spacing-03);
  flex-wrap: wrap;
`;

const TimelineDetailsContainer = styled.div`
  width: 100%;
  /* 560 + 120 + 120 */
  max-width: 800px;
`;

const TimelineSection = styled(FlexWrapperWithShortGapColumn)`
  padding: var(--spacing-09) var(--meeting-details-padding);
  scroll-margin-top: 20px;
  min-height: 200px;
  border: 1px solid #f4f4f4;
  border-radius: 16px;
  margin: var(--spacing-06) 0;
  background-color: #fff;
  &:hover {
    border: 1px solid #f7f7f7;
  }
`;

export const KryptonMenuIcon = styled(FontAwesomeIcon).attrs({
  size: 24,
  isGradient: true,
})``;

interface TimelineDetailsProps {
  timelines: Timeline[];
}

export const TimelineDetails = memo(({ timelines }: TimelineDetailsProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const translate = useTranslate();

  useEffect(() => {
    const observer = observerRef.current;

    if (observer) {
      const elements = document.querySelectorAll('[data-timeline-id]');

      elements.forEach((element) => observer.observe(element));
    }
  }, [timelines]);

  return (
    <TimelineDetailsContainer>
      {timelines?.map((timeline) =>
        timeline.summaryAvailable ? (
          <TimelineSection
            key={timeline?.source?.id}
            data-timeline-id={timeline?.source?.id}
          >
            <TimelineHeader>
              <FlexWrapperWithJustifyBetween>
                <FlexWrapperWithShortGap>
                  <Body01>
                    <HumanisedDateWithYear
                      timestamp={moment(timeline?.startTime ?? '')}
                    />
                  </Body01>
                  <Body02>
                    <HumanisedTime
                      timestamp={moment(timeline?.startTime ?? '')}
                    />
                  </Body02>
                </FlexWrapperWithShortGap>
                {/* <>
                    <AiIndicator aiGenerated={true} size="sm" />
                  </> */}
              </FlexWrapperWithJustifyBetween>

              <FlexWrapperWithShortGap>
                <KryptonMenuIcon
                  icon={
                    timeline?.source?.type === 'EMAIL' ? faEnvelope : faCalendar
                  }
                  color={
                    timeline?.source?.type === 'EMAIL' ? 'magenta' : 'blue'
                  }
                />
                <ProductiveHeading03>
                  {timeline?.source?.title}
                </ProductiveHeading03>
              </FlexWrapperWithShortGap>
            </TimelineHeader>

            {/* <FlexWrapperAlignCenter>
                <Collapse
                  style={{
                    width: '100%',
                  }}
                  expandIcon={({ isActive }) => {
                    return isActive ? (
                      <FontAwesomeIcon icon={faChevronDown} />
                    ) : (
                      <FontAwesomeIcon icon={faChevronRight} />
                    );
                  }}
                >
                  <Panel
                    header={
                      <ProductiveHeading01>Timeline Details</ProductiveHeading01>
                    }
                    key="1"
                  >
                    <DetailRow>
                      <Label>Date</Label>
                      <Value>
                        <HumanisedDate
                          timestamp={moment(timeline?.startTime ?? '')}
                        />
                      </Value>
                    </DetailRow>
                    <DetailRow>
                      <Label>Participants</Label>
                      <Tooltip
                        title={timeline?.meta?.participants
                          ?.reduce((names, participant) => {
                            if (
                              participant?.firstName ||
                              participant?.lastName
                            ) {
                              names.push(
                                `${participant?.firstName ?? ''} ${participant?.lastName ?? ''}`,
                              );
                            }

                            return names;
                          }, [] as string[])
                          ?.join(', ')}
                      >
                        <Value>
                          {timeline?.meta?.participants?.[0]?.firstName}{' '}
                          {timeline?.meta?.participants?.[0]?.lastName}
                          {timeline?.meta?.participants?.length > 1 &&
                            ` and ${timeline?.meta?.participants?.length - 1} others`}
                        </Value>
                      </Tooltip>
                    </DetailRow>
                  </Panel>
                </Collapse>
              </FlexWrapperAlignCenter> */}

            <RecapSection>
              <RecapTitle>
                <FontAwesomeIcon icon={faAlignLeft} />
                <ProductiveHeading01>
                  {timeline?.source?.type === 'EMAIL'
                    ? translate('accounts.page.timeline.email_recap')
                    : translate('accounts.page.timeline.meeting_recap')}
                </ProductiveHeading01>
              </RecapTitle>
              <RecapText>
                {timeline?.signalAwareRecap || timeline?.summaryAvailable ? (
                  timeline?.signalAwareRecap ? (
                    <Fragment>{timeline?.signalAwareRecap}</Fragment>
                  ) : (
                    timeline?.summary
                  )
                ) : null}
              </RecapText>
              <TagsContainer>
                {timeline?.detectedSignals?.map((signal, index) => (
                  <Tag
                    key={signal?.signalConfigurationId || index}
                    color={signalTypeConfigs[signal?.signalType]?.color}
                    style={{
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      const signalElement = document.getElementById(
                        `signal-${signal?.signalName?.toLowerCase()}-${timeline?.source?.type} - ${timeline?.source?.id}`,
                      );

                      if (signalElement) {
                        signalElement.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }
                    }}
                  >
                    {signal?.signalName}
                  </Tag>
                ))}
              </TagsContainer>
            </RecapSection>

            {timeline?.signalOccurrences?.length === 0 ? (
              <EmptyState
                title="No signals have been detected"
                image={
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    size={40}
                    style={{ color: '#8D8D8D' }}
                  />
                }
              />
            ) : (
              timeline?.signalOccurrences?.map((signal) => {
                const reason = signal?.fields?.find(
                  (field) =>
                    Object.keys(signal?.metaData)[0] === String(field?.fieldId),
                );

                return (
                  <SignalOutputCard
                    key={signal?.signalOccurrenceId}
                    sourceId={signal?.sourceId}
                    sourceType={signal?.sourceType}
                    people={signal?.reportedBy ?? []}
                    reason={reason}
                    title={signal?.title}
                    description={signal?.summary}
                    tasks={[]}
                    insightfulContent={''}
                    citations={signal?.citationMapping}
                    signalType={
                      // TODO: Make this as map
                      timeline?.detectedSignals?.find(
                        (detSig) =>
                          detSig?.signalConfigurationId ===
                          signal?.signalConfigurationId,
                      )?.signalType
                    }
                    type={
                      timeline?.detectedSignals?.find(
                        (detSig) =>
                          detSig?.signalConfigurationId ===
                          signal?.signalConfigurationId,
                      )?.signalName ?? ''
                    }
                    className="timeline-details-signal"
                  />
                );
              })
            )}
          </TimelineSection>
        ) : null,
      )}
    </TimelineDetailsContainer>
  );
});

TimelineDetails.displayName = 'TimelineDetails';
